import React, { useState, useRef, useCallback } from 'react';
import DraggableComponent from '../components/DraggableComponent';
import Button from '../components/Button';
import Panel from '../components/Panel';
import './Canvas.css';

const Canvas = ({ 
  components = [], 
  onComponentUpdate, 
  onComponentSelect, 
  onComponentClick, 
  selectedComponent, 
  selectedProfile, 
  canvasProperties = { backgroundColor: '#383838' },
  canvasSize = { width: 1024, height: 768 },
  isDraggable = true, // New prop to control dragging behavior
  accessibilityOptions = {}, // Add accessibility options prop
  currentPageName,
  setCaptionText,
  captionText
}) => {
  const canvasRef = useRef(null);

  // Check if dark mode is active
  const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
  
  // Dynamic background styling based on dark mode and canvas properties
  const getCanvasBackgroundStyles = () => {
    const backgroundType = canvasProperties?.backgroundType || 'solid';
    
    if (backgroundType === 'image' && canvasProperties?.backgroundImage) {
      return {
        backgroundImage: `url(${canvasProperties.backgroundImage})`,
        backgroundSize: 'contain', // Always size to fit - maintains aspect ratio, shows full image
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: 'transparent',
        // High-quality image scaling property with fallbacks
        imageRendering: 'auto' // Let browser choose best quality
      };
    } else {
      // Solid color background logic
      if (canvasProperties?.backgroundColor) {
        return { backgroundColor: canvasProperties.backgroundColor };
      }
      
      if (selectedProfile?.colors?.background) {
        return { backgroundColor: selectedProfile.colors.background };
      }
      
      // Theme-appropriate default
      return { backgroundColor: isDarkMode ? '#1a1a1a' : '#383838' };
    }
  };

  const getCanvasTextColor = () => {
    // If profile has custom text color, use it
    if (selectedProfile?.colors?.text) {
      return selectedProfile.colors.text;
    }
    
    // Otherwise, use theme-appropriate default
    return isDarkMode ? '#e0e0e0' : '#000000';
  };
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e) => {
    // Only handle clicks on the canvas background (not on components)
    if (e.target.closest('.canvas-component')) return;
    
    // Deselect all components when clicking empty canvas
    console.log('Canvas background clicked - deselecting all components');
    onComponentSelect?.(null);
  }, [onComponentSelect]);

  const handleComponentDrag = useCallback((componentId, updates) => {
    // Apply updates directly without snapping for now
    console.log('Canvas handleComponentDrag called:', componentId, updates);
    
    // Apply grid snapping if enabled and position was updated
    if (updates.position && canvasProperties?.snapToGrid && isDraggable) {
      const gridSize = canvasProperties?.gridSize || 20;
      // Move snapToGrid logic inline to avoid dependency issues
      const snappedPosition = {
        x: Math.round(updates.position.x / gridSize) * gridSize,
        y: Math.round(updates.position.y / gridSize) * gridSize
      };
      updates.position = snappedPosition;
      console.log('🔲 Applied grid snapping:', updates.position, 'Grid size:', gridSize);
    }
    
    // Check for parent-child relationship if position was updated
    if (updates.position && isDraggable) {
      const draggedComponent = components.find(c => c.id === componentId);
      if (draggedComponent) {
        // Store the position delta for moving children
        const positionDelta = {
          x: updates.position.x - draggedComponent.position.x,
          y: updates.position.y - draggedComponent.position.y
        };

        // Check if the component is being dropped over a panel
        const potentialParent = findPotentialParent(draggedComponent, updates.position, components);
        if (potentialParent && potentialParent.id !== draggedComponent.parentId) {
          // Create parent-child relationship
          updates.parentId = potentialParent.id;
          updates.zIndex = potentialParent.zIndex + 1; // Child should be above parent
          console.log(`🔗 Creating parent-child relationship: ${componentId} -> ${potentialParent.id}`);
        } else if (!potentialParent && draggedComponent.parentId) {
          // Remove from parent if no longer over a panel
          updates.parentId = null;
          updates.zIndex = Date.now(); // Reset to current time
          console.log(`🔓 Removing parent-child relationship: ${componentId}`);
        }

        // Move all children with this component if it has children
        if (draggedComponent.children && draggedComponent.children.length > 0 && (positionDelta.x !== 0 || positionDelta.y !== 0)) {
          console.log(`🚶 Moving ${draggedComponent.children.length} children with parent ${componentId}`);
          
          // Update children positions relative to parent movement
          draggedComponent.children.forEach(childId => {
            const childComponent = components.find(c => c.id === childId);
            if (childComponent) {
              const newChildPosition = {
                x: childComponent.position.x + positionDelta.x,
                y: childComponent.position.y + positionDelta.y
              };
              
              // Use setTimeout to avoid recursive updates during the same render cycle
              setTimeout(() => {
                onComponentUpdate?.(childId, { position: newChildPosition });
              }, 0);
            }
          });
        }
      }
    }
    
    onComponentUpdate?.(componentId, updates);
  }, [onComponentUpdate, components, isDraggable, canvasProperties]);

  // Function to detect if a component is being dropped over a panel
  const findPotentialParent = (draggedComponent, newPosition, allComponents) => {
    const draggedBounds = {
      left: newPosition.x,
      top: newPosition.y,
      right: newPosition.x + draggedComponent.size.width,
      bottom: newPosition.y + draggedComponent.size.height
    };

    // Find panels that the dragged component overlaps with
    const overlappingPanels = allComponents.filter(component => {
      // Only consider panels as potential parents
      if (component.type !== 'panel' || component.id === draggedComponent.id) {
        return false;
      }

      const panelBounds = {
        left: component.position.x,
        top: component.position.y,
        right: component.position.x + component.size.width,
        bottom: component.position.y + component.size.height
      };

      // Check if dragged component center is inside the panel
      const draggedCenterX = draggedBounds.left + draggedComponent.size.width / 2;
      const draggedCenterY = draggedBounds.top + draggedComponent.size.height / 2;

      return (
        draggedCenterX >= panelBounds.left &&
        draggedCenterX <= panelBounds.right &&
        draggedCenterY >= panelBounds.top &&
        draggedCenterY <= panelBounds.bottom
      );
    });

    // Return the top-most panel (highest z-index) if multiple panels overlap
    if (overlappingPanels.length > 0) {
      return overlappingPanels.reduce((topPanel, panel) => 
        panel.zIndex > topPanel.zIndex ? panel : topPanel
      );
    }

    return null;
  };

  const backgroundStyles = getCanvasBackgroundStyles();

  console.log('Current page name in Canvas:', currentPageName);

  return (
    <div 
      className="canvas"
      aria-label={`Page ${currentPageName}`}
      ref={canvasRef}
      onMouseDown={handleMouseDown}
      style={{
        width: `${canvasSize.width}px`,
        height: `${canvasSize.height}px`,
        ...backgroundStyles,
        color: getCanvasTextColor(),
        position: 'relative'
      }}
    >
      <div 
        className={`canvas-grid ${canvasProperties?.gridEnabled ? '' : 'hidden'}`}
        style={{
          '--grid-size': `${canvasProperties?.gridSize || 20}px`,
          '--grid-opacity': canvasProperties?.gridOpacity || 0.3
        }}
        aria-hidden="true"
      ></div>

      {/* Caption popup overlay at bottom center of canvas */}
      {captionText && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: '24px',
            transform: 'translateX(-50%)',
            minWidth: '200px',
            maxWidth: '80%',
            minHeight: 40,
            maxHeight: 80,
            overflow: 'hidden',
            transition: 'max-height 0.3s',
            background: 'rgba(0,0,0,0.85)',
            color: '#fff',
            padding: '12px 18px',
            fontSize: '1.15em',
            textAlign: 'center',
            borderRadius: '10px',
            zIndex: 1000,
            boxShadow: '0 2px 12px rgba(0,0,0,0.25)'
          }}
          aria-live="polite"
          role="status"
        >
          {captionText}
        </div>
      )}
      
      {components.map(component => {
        // Use the appropriate component type based on component.type
        let ComponentToRender;
        
        switch(component.type) {
          case 'button':
            ComponentToRender = Button;
            break;
          case 'panel':
            ComponentToRender = Panel;
            break;
          default:
            ComponentToRender = DraggableComponent;
            break;
        }
        
        // Pass setCaptionText only to Button
        const extraProps = {};
        if (component.type === 'button' && typeof setCaptionText === 'function') {
          extraProps.setCaptionText = setCaptionText;
        }
        return (
          <ComponentToRender
            key={component.id}
            component={component}
            canvasProperties={canvasProperties}
            isSelected={selectedComponent?.id === component.id}
            onDrag={handleComponentDrag}
            onSelect={() => {
              console.log('Canvas onSelect called for component:', component.id);
              onComponentSelect(component.id);
            }}
            onAction={onComponentClick}
            canvasSize={canvasSize}
            isDraggable={isDraggable}
            accessibilityOptions={accessibilityOptions}
            disableLongPress={!isDraggable}
            disableResize={!isDraggable}
            {...extraProps}
          />
        );
      })}
      
      {components.length === 0 && (
        <div className="canvas-empty" role="status" aria-live="polite">
          <h3>🎨 Canvas is Empty</h3>
          <p>
            {selectedProfile 
              ? `Drag components to create your ${selectedProfile.name} interface`
              : 'Drag components from the toolbox to get started'
            }
          </p>
          {selectedProfile && (
            <p style={{ fontSize: '14px', opacity: 0.7 }}>
              Using {selectedProfile.name} accessibility profile
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default Canvas;